import React from "react";
import { Navigate, useParams } from "react-router-dom";
import useAsyncRequest from "hooks/useAsyncRequest";
import { LoadablePage } from "shared/LoadablePage";
import { getServerLicenseById, getServerLicenseBySerial } from "client/api/ServerLicenseApi";
import type { ServerLicenseDto } from "client/api/ServerLicenseApi";
import { NotFoundPage } from "pages/NotFoundPage";
import { licenseIdRouteParam } from "areas/licenses/license-routes";
import { subscriptionRoutes } from "areas/subscriptions/subscription-routes";

function resolveSubscriptionId(serverLicense: ServerLicenseDto): string | null {
  return serverLicense.licensingChannel.subscriptionId;
}

const unmappedRecordMessage =
  "This record cannot be opened in the unified view — it is not linked to a subscription. Log an issue.";

function RedirectToUnified({ serverLicense }: { serverLicense: ServerLicenseDto }) {
  const subscriptionId = resolveSubscriptionId(serverLicense);
  if (!subscriptionId) {
    return <NotFoundPage message={unmappedRecordMessage} />;
  }
  return <Navigate to={subscriptionRoutes.detail(subscriptionId).root} replace />;
}

export function ServerLicenseRedirect() {
  const params = useParams();
  const licenseId = params[licenseIdRouteParam];

  const { data, loading, error } = useAsyncRequest(getServerLicenseById, licenseId || "");

  if (!licenseId) {
    return <NotFoundPage />;
  }

  return (
    <LoadablePage loading={loading} error={error}>
      {data ? <RedirectToUnified serverLicense={data} /> : <NotFoundPage />}
    </LoadablePage>
  );
}

export function ServerLicenseBySerialRedirect() {
  const { serialId } = useParams();
  const { data, loading, error } = useAsyncRequest(getServerLicenseBySerial, serialId || "");

  if (!serialId) {
    return <NotFoundPage />;
  }

  return (
    <LoadablePage loading={loading} error={error}>
      {data ? <RedirectToUnified serverLicense={data} /> : <NotFoundPage />}
    </LoadablePage>
  );
}
